import type { ReactNode } from 'react';
import { Box } from './box';
import { RAIL_MARGIN, RAIL_WIDTH } from '../../helpers/utils/screenFit';

interface WideColumnsProps {
  /** The stage (a FitStage), centred in the left column. */
  stage: ReactNode;
  /** The 460pt right rail. */
  rail: ReactNode;
}

/**
 * Desktop (>= 1024): the stage on the left, the rail on the right (Figma D1,
 * e.g. 1728:103869). Both start 16 under the wide header; the rail stops 24
 * above the bottom and 24 from the right edge, at x = width - 484. The left
 * column only scrolls if the stage can't fit (Classic on a short window).
 *
 * That scrolling clips the stage's felt glow, so the stage's scroller spans
 * the whole area under the header, behind the rail, and pads its content
 * into the left column. The glow then fades out under the header's edge and
 * the opaque rail instead of stopping at a hard line (2026-10-22).
 */
function WideColumns({ stage, rail }: WideColumnsProps) {
  return (
    <Box
      data-testid="wide-columns"
      display="grid"
      gridTemplateColumns={`minmax(0, 1fr) ${RAIL_WIDTH}px ${RAIL_MARGIN}px`}
      gridTemplateRows="minmax(0, 1fr)"
      flex="1"
      minH={0}
    >
      <Box
        gridColumn="1 / -1"
        gridRow="1"
        minW={0}
        minH={0}
        overflowX="hidden"
        overflowY="auto"
        pt="var(--spades-spacing-md)"
        pr={`${RAIL_WIDTH + RAIL_MARGIN}px`}
      >
        {stage}
      </Box>
      <Box
        gridColumn="2"
        gridRow="1"
        minH={0}
        display="flex"
        flexDirection="column"
        pt="var(--spades-spacing-md)"
        // Over the stage's scroller, which runs behind it.
        position="relative"
        pointerEvents="none"
      >
        {rail}
      </Box>
    </Box>
  );
}

export default WideColumns;
